import { Link } from 'react-router-dom'
import { ArrowRight, Plus } from 'lucide-react'
import { StaggerContainer, StaggerItem } from '@/components/motion'
import { agents } from '@/components/landing/AITeamOrbit'

const byName = Object.fromEntries(agents.map((a) => [a.name, a]))

const STEPS = [
  {
    agent: 'Dimi',
    title: 'Dimi drafts',
    description: 'Share the client and the document type. Dimi builds the first draft from your own templates.',
  },
  {
    agent: 'Alex',
    title: 'Alex finds',
    description: 'Clauses, figures, case studies and approved images, pulled from your libraries and past documents.',
  },
  {
    agent: 'Sia',
    title: 'Sia polishes',
    description: 'Rough text becomes clear prose in your house voice, without leaving Word.',
  },
  {
    agent: 'Scarlet',
    title: 'Scarlet checks',
    description: 'Fonts, colours, logos, tone and required wording, checked before anything goes out.',
  },
]

/**
 * How the orbit's agents turn a brief into a finished document: four agent
 * steps, then a fifth for building your own agents in AI Studio. Sits
 * inside the orbit section, under the globe.
 */
export function AgentSteps() {
  return (
    <div className="landing-container relative z-10 border-t border-[var(--color-landing-gray-200)] pb-20 pt-14 dark:border-[var(--color-landing-gray-800)] md:pb-28 md:pt-20">
      <StaggerContainer amount={0.1} className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map((step) => {
          const agent = byName[step.agent]!
          return (
            <StaggerItem key={step.agent} className="flex flex-col">
              <span className="h-16 w-16 overflow-hidden" style={{ backgroundColor: agent.color }}>
                <img src={agent.avatar} alt="" className="h-full w-full translate-y-[6%] scale-110 object-cover" />
              </span>
              <h3 className="mt-6 font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.35] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
                {step.title}
              </h3>
              <p className="landing-text-body-sm mt-3 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                {step.description}
              </p>
            </StaggerItem>
          )
        })}

        {/* Fifth step: your own agents */}
        <StaggerItem className="flex flex-col sm:col-span-2 lg:col-span-1">
          {/* A chain of agents ending in one of your own */}
          <span aria-hidden className="flex h-16 items-center">
            {['Olga', 'Andrew'].map((name) => (
              <span key={name} className="flex items-center">
                <span className="h-12 w-12 overflow-hidden" style={{ backgroundColor: byName[name]!.color }}>
                  <img src={byName[name]!.avatar} alt="" className="h-full w-full translate-y-[6%] scale-110 object-cover" />
                </span>
                <span className="block h-[2px] w-5 bg-[var(--color-landing-gray-400)]" />
              </span>
            ))}
            <span className="flex h-12 w-12 items-center justify-center bg-[#0a0a0a] text-white dark:bg-white dark:text-[#0a0a0a]">
              <Plus className="h-6 w-6" strokeWidth={2.5} />
            </span>
          </span>
          <h3 className="mt-6 font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.35] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
            You build more
          </h3>
          <p className="landing-text-body-sm mt-3 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
            Encode your own processes and rules as agents, and chain them in the AI Studio workflow builder.
          </p>
          <Link
            to="/platform/ai-generation"
            className="group mt-4 inline-flex items-center gap-2 font-semibold text-[var(--color-landing-accent)] dark:text-[#8a84ff]"
          >
            See AI Studio
            <ArrowRight aria-hidden className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </StaggerItem>
      </StaggerContainer>
    </div>
  )
}

export default AgentSteps
